import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// ============================================================
// MATRIZ PADRÃO — fallback enquanto Firestore carrega ou
// se config_permissoes/default ainda não foi salvo.
// Fonte de verdade em produção: doc Firestore (via onSnapshot).
// ============================================================
const TODOS           = ['GESTOR', 'OPERADOR', 'VISUALIZADOR'];
const GESTOR_OPERADOR = ['GESTOR', 'OPERADOR'];
const SO_GESTOR       = ['GESTOR'];

const MATRIZ_PADRAO = {
  // ---------- Navegação ----------
  verDashboard:    TODOS,
  verEstoque:      TODOS,
  verOS:           TODOS,
  verRelatorios:   TODOS,
  verHistorico:    TODOS,            // histórico de movimentação (separado do menu/form)
  verMovimentacao: GESTOR_OPERADOR,  // menu Movimentação + form nova movimentação
  verCadastros:    SO_GESTOR,
  verConfiguracoes: SO_GESTOR,

  // ---------- Movimentação ----------
  movimentar:          GESTOR_OPERADOR,
  aprovarMovimentacao: GESTOR_OPERADOR,

  // ---------- OS ----------
  criarOS:    GESTOR_OPERADOR,
  editarOS:   GESTOR_OPERADOR,
  excluirOS:  SO_GESTOR,             // fixo — não exposto na tela de Configurações
  concluirOS: GESTOR_OPERADOR,

  // ---------- Cadastros ----------
  cadastrarProduto: SO_GESTOR,
  editarProduto:    SO_GESTOR,
  inativarProduto:  SO_GESTOR,
  cadastrarEstoque: SO_GESTOR,
  editarEstoque:    SO_GESTOR,
  cadastrarUsuario: SO_GESTOR,
  editarUsuario:    SO_GESTOR,

  // ---------- Configurações ----------
  alterarParametros: SO_GESTOR,
  alterarPermissoes: SO_GESTOR,
};

// ============================================================
// Ponte: ID do item em Configurações → ações do MATRIZ
// Gestor é sempre incluído; esta tabela só controla
// OPERADOR e VISUALIZADOR via flags booleanos do Firestore.
// Ações não mapeadas (ex: excluirOS) ficam fixas em ['GESTOR'].
// ============================================================
const ID_PARA_ACOES = {
  'dashboard':      ['verDashboard'],
  'estoque':        ['verEstoque'],
  'mov-saida':      ['movimentar', 'verMovimentacao'],
  'mov-edt':        ['movimentar', 'verMovimentacao'],
  'mov-aceite':     ['aprovarMovimentacao', 'verMovimentacao'],
  'mov-hist':       ['verHistorico'],    // histórico separado — não dá acesso ao menu/form
  'os-consulta':    ['verOS'],
  'os-editar':      ['criarOS', 'editarOS', 'concluirOS'],
  'cad-prod':       ['verCadastros', 'cadastrarProduto', 'editarProduto', 'inativarProduto'],
  'cad-estruturas': ['verCadastros', 'cadastrarEstoque', 'editarEstoque'],
  'cad-users':      ['verCadastros', 'cadastrarUsuario', 'editarUsuario'],
  'sis-rel':        ['verRelatorios'],
  'sis-config':     ['verConfiguracoes', 'alterarParametros', 'alterarPermissoes'],
};

// Constrói MATRIZ de runtime a partir dos grupos lidos do Firestore.
// Ponto de partida: todos os Gestores (inalterável).
// OPERADOR e VISUALIZADOR são adicionados conforme os flags do doc.
function construirMatriz(grupos) {
  const m = {};
  for (const acao in MATRIZ_PADRAO) {
    m[acao] = ['GESTOR'];
  }
  for (const grupo of grupos) {
    for (const item of (grupo.itens || [])) {
      for (const acao of (ID_PARA_ACOES[item.id] || [])) {
        if (!m[acao]) m[acao] = ['GESTOR'];
        if (item.operador     && !m[acao].includes('OPERADOR'))     m[acao].push('OPERADOR');
        if (item.visualizador && !m[acao].includes('VISUALIZADOR')) m[acao].push('VISUALIZADOR');
      }
    }
  }
  return m;
}

function normalizar(p) {
  return (p || '').toString().trim().toUpperCase();
}

// ============================================================
// Hook público — API mantida idêntica à versão anterior
// ============================================================
export function usePermissao() {
  const perfilStore = useAuthStore((s) => s.perfil);
  const perfil = normalizar(perfilStore);

  // Estado inicial = MATRIZ_PADRAO → sem flash de loading antes do Firestore responder
  const [matriz, setMatriz] = useState(MATRIZ_PADRAO);

  useEffect(() => {
    if (!perfil) return; // não assina sem sessão ativa

    const unsub = onSnapshot(
      doc(db, 'config_permissoes', 'default'),
      (snap) => {
        if (snap.exists() && Array.isArray(snap.data().matriz)) {
          setMatriz(construirMatriz(snap.data().matriz));
        } else {
          setMatriz(MATRIZ_PADRAO); // doc não existe → mantém padrão hardcoded
        }
      },
      (err) => {
        console.error('[usePermissao] Erro ao carregar permissões dinâmicas:', err);
        // mantém o estado atual — já é o fallback seguro
      }
    );

    return unsub; // limpa listener no logout ou troca de perfil
  }, [perfil]);

  // Gestor tem acesso total e não depende da matriz
  function pode(acao) {
    if (!perfil) return false;
    if (perfil === 'GESTOR') return true;
    if (Array.isArray(acao)) {
      return acao.every((a) => (matriz[a] || []).includes(perfil));
    }
    return (matriz[acao] || []).includes(perfil);
  }

  function podeQualquer(acoes) {
    if (!perfil) return false;
    if (perfil === 'GESTOR') return true;
    return acoes.some((a) => (matriz[a] || []).includes(perfil));
  }

  return {
    perfil,
    ehGestor:       perfil === 'GESTOR',
    ehOperador:     perfil === 'OPERADOR',
    ehVisualizador: perfil === 'VISUALIZADOR',
    pode,
    podeQualquer,
  };
}
